import { AbsoluteTimeRange } from '@chronosphereio/core';
import {
  EventItem,
  FormatValueOptions,
  formatValue,
  HeatMapAxisScale,
  HeatMapPropsBase,
  HeatMapSpec,
  HeatMapTimeSeriesData,
  HeatMapYAxisOptions,
  HeatmapColorOptions,
  HeatmapLegendOptions,
  HeatmapStatLineType,
  isBytesUnit,
  isDecimalUnit,
  MIN_STEP_FALLBACK_MS,
  SCIENTIFIC_NOTATION_FORMATTER,
  TimeScale,
  UnitOptions,
} from '@/core';
import { BasePanelProps, OptionsEditorProps, QueryData } from '@/plugin-system';
import { CustomColorScale } from './color-scales';

export const DEFAULT_UNIT: UnitOptions = {
  kind: 'Decimal',
  abbreviate: true,
};

export const DEFAULT_Y_AXIS_OPTIONS: HeatMapYAxisOptions = {
  unit: DEFAULT_UNIT,
  label: '',
  show: true,
};

export const DEFAULT_COLOR_OPTIONS: HeatmapColorOptions = { scale: 'orangeRed', invertScale: false, hideZeros: true };

export const DEFAULT_LEGEND_OPTIONS: HeatmapLegendOptions = { show: true, position: 'Right' };

/**
 * Props for the heatmap options editor
 */
export type HeatMapOptionsEditorProps = OptionsEditorProps<HeatMapSpec>;

export type TrimBoundMode = 'Value' | 'Percentile';

/**
 * Types for the HeatMap that are only used in the `Viz` variant.
 *
 * @see {@link HeatMapPropsBase} for types defined in the schema because they are also shared with
 * the `DashboardPanel` variant.
 */
interface HeatMapPropsVizOnly {
  /**
   * Allows consumers to provide a custom color scale and getColor function.
   * This overrides `scale` defined in the spec.
   */
  customColorScale?: CustomColorScale;
  /**
   * Click handler for selected event
   */
  onEventClick?: (e: EventItem) => void;
  /**
   * Y-axis scale type for the explorer mode rendering path.
   * When set, overrides the schema-level `y_axis.scale` for the Viz variant.
   */
  yAxisScale?: HeatMapAxisScale;
  /**
   * Trim range for explorer mode Y-axis bounds.
   * Viz-only -- not persisted to dashboard spec.
   */
  trimRange?: {
    min_mode?: TrimBoundMode;
    max_mode?: TrimBoundMode;
    min?: number;
    max?: number;
  };
  /**
   * Statistical line overlays (explorer-only, not persisted to dashboard spec yet).
   */
  statLines?: HeatmapStatLineType[];
  /**
   * Override the sync group used for shared crosshair interactions.
   * By default, the sync group is derived from the app metadata context.
   * Set to a custom string to use a specific group, or `false` to opt out of syncing.
   */
  syncGroup?: string | false;
}

/**
 * Props for the heatmap panel
 *
 * Intersection (not `interface extends`) so `BasePanelProps` fields like `panelKey` are not lost
 * to odd interface merge behavior with schema-derived `HeatMapPropsBase`.
 */
export type HeatMapPanelProps = BasePanelProps & HeatMapPropsBase & HeatMapPropsVizOnly;

/**
 * Bucket definition
 */
export interface HeatMapChartBucket {
  /**
   * lower boundary of the bucket
   */
  lower: number;
  /**
   * upper boundary of the bucket
   */
  upper: number;
}

/**
 * Value for a specific time and bucket
 */
export interface HeatMapChartDataPoint {
  /**
   * numeric value (histogram count)
   */
  value: number;
  /**
   * offset in the buckets array
   */
  bucketIndex: number;
  /**
   * number of buckets spanned by this datapoint (this may be > 1 for varying bucket sizes)
   */
  bucketSpan: number;
}

/**
 * Dataset at a specific point in time
 */
export interface ChartTimeSlice {
  /**
   * timestamp of the data (milliseconds)
   */
  timestamp: number;
  /**
   * histogram data at the timestamp
   */
  dataPoints: HeatMapChartDataPoint[];
}

/**
 * Data structure used for rendering a heatmap visualization
 */
export interface HeatMapChartData {
  /**
   * maximum value found in any cell of the data
   */
  maxValue: number;
  /**
   * time range corresponding to the query (timeSlices may not fill entire range)
   */
  timeRange: TimeScale;
  /**
   * definitions of the data buckets (for data with varying bucket sizes these will be the smallest bucket sizes)
   */
  buckets: HeatMapChartBucket[];
  /**
   * data at each timestamp
   */
  timeSlices: ChartTimeSlice[];
}

// Derive an ID from a bucket -- simply convert the upper boundary value to string
function bucketId(upper: number) {
  return upper.toString();
}

// Calculate the bucket position (aka index) for a given upper bound and base
function getExponentialBucketPosition(upperBound: number, base: number) {
  const position = Math.round(Math.log(Math.abs(upperBound)) / Math.log(base) - 1);
  // for negative bounds, the position formula returns the position for the bucket below, so we add 1
  return upperBound < 0 ? position + 1 : position;
}

// Build a set of buckets by checking all data points
// Also check for varying bucket sizes
function findBuckets(data: HeatMapTimeSeriesData) {
  const bucketSet: Record<string, HeatMapChartBucket> = {};
  for (let i = 0; i < data.heatmap.length; i++) {
    const heatmap = data.heatmap[i];
    if (!heatmap) continue;
    for (let j = 0; j < heatmap.buckets.length; j++) {
      const bucket = heatmap.buckets[j];
      if (!bucket) continue;
      const id = bucketId(bucket.upperBound);
      const match = bucketSet[id];
      if (match && match.lower !== bucket.lowerBound) {
        return { isVaryingBucketSizes: true };
      }
      bucketSet[id] = { upper: bucket.upperBound, lower: bucket.lowerBound };
    }
  }
  return { bucketSet };
}

/**
 * Numeric range
 */
interface Range {
  /**
   * Maximum value
   */
  max: number;
  /**
   * Minimum value
   */
  min: number;
}

// find minimum exponential base as well as overall range of bucket values (min->max) for positive and negative buckets
function findExponentialInfo(data: HeatMapTimeSeriesData) {
  let base = Number.POSITIVE_INFINITY;
  let rangePositive: Range | undefined;
  let rangeNegative: Range | undefined;

  // a zero bucket is a special bucket with lower < 0 and upper > 0
  let zeroBucket: { lower: number; upper: number } | undefined;

  for (let i = 0; i < data.heatmap.length; i++) {
    const heatmap = data.heatmap[i];
    if (!heatmap) continue;

    // base is the same for all buckets at a timestamp, so once we find the base there is
    // no need to check any other buckets in the heatmap
    let baseFound = false;
    for (let j = 0; j < heatmap.buckets.length; j++) {
      const bucket = heatmap.buckets[j];
      if (!bucket) continue;

      if (!baseFound) {
        // invert the base if upper & lower are negative
        const bucketBase =
          bucket.upperBound >= 0 ? bucket.upperBound / bucket.lowerBound : bucket.lowerBound / bucket.upperBound;
        // base cannot be calculated from a bucket that crosses 0 (upper > 0, lower < 0)
        if (bucketBase >= 0) {
          baseFound = true;
          base = Math.min(bucketBase, base);
        }
      }

      if (bucket.upperBound < 0) {
        // negative-boundary bucket: update negative range
        if (!rangeNegative) {
          rangeNegative = {
            max: Number.NEGATIVE_INFINITY,
            min: 0,
          };
        }
        rangeNegative.max = Math.max(bucket.upperBound, rangeNegative.max);
        rangeNegative.min = Math.min(bucket.lowerBound, rangeNegative.min);
      } else if (bucket.lowerBound > 0) {
        // positive-boundary bucket: update positive range
        if (!rangePositive) {
          rangePositive = {
            max: 0,
            min: Number.POSITIVE_INFINITY,
          };
        }
        rangePositive.max = Math.max(bucket.upperBound, rangePositive.max);
        rangePositive.min = Math.min(bucket.lowerBound, rangePositive.min);
      } else if (!zeroBucket) {
        zeroBucket = {
          lower: bucket.lowerBound,
          upper: bucket.upperBound,
        };
      }
    }
  }
  return { base, rangePositive, rangeNegative, zeroBucket };
}

// build list of exponential buckets covering entire range, using the minimum base
function buildExponentialBuckets(
  base: number,
  rangePositive: Range | undefined,
  rangeNegative: Range | undefined,
  zeroBucket: { lower: number; upper: number } | undefined
) {
  const buckets: HeatMapChartBucket[] = [];

  if (rangeNegative) {
    const minBucketIndex = getExponentialBucketPosition(rangeNegative.min, base) - 1;
    const maxBucketIndex = getExponentialBucketPosition(rangeNegative.max, base);
    for (let i = minBucketIndex; i >= maxBucketIndex; i--) {
      buckets.push({
        lower: -Math.pow(base, i + 1),
        upper: -Math.pow(base, i),
      });
    }
  }
  const numNegativeBuckets = buckets.length;

  if (zeroBucket) {
    buckets.push(zeroBucket);
  }

  if (rangePositive) {
    const minBucketIndex = 1 + getExponentialBucketPosition(rangePositive.min, base);
    const maxBucketIndex = getExponentialBucketPosition(rangePositive.max, base);
    for (let i = minBucketIndex; i <= maxBucketIndex; i++) {
      buckets.push({
        lower: Math.pow(base, i),
        upper: Math.pow(base, i + 1),
      });
    }
  }
  return { buckets, numNegativeBuckets };
}

// Build HeatMapChartData from data with varying bucket sizes
function processExponentialBuckets(timeRange: TimeScale, data: HeatMapTimeSeriesData) {
  let maxValue = 0;
  const timeSlices: ChartTimeSlice[] = [];
  const { base, rangePositive, rangeNegative, zeroBucket } = findExponentialInfo(data);
  const { buckets, numNegativeBuckets } = buildExponentialBuckets(base, rangePositive, rangeNegative, zeroBucket);
  const firstNegativeBucketPosition = rangeNegative ? getExponentialBucketPosition(rangeNegative.min, base) : 0;
  const firstPositiveBucketPosition = rangePositive ? getExponentialBucketPosition(rangePositive.min, base) : 0;

  // process datapoints
  for (let i = 0; i < data.heatmap.length; i++) {
    const heatmap = data.heatmap[i];
    if (!heatmap) continue;
    const timeSlice: ChartTimeSlice = { timestamp: heatmap.timestamp, dataPoints: [] };
    timeSlices.push(timeSlice);
    for (let j = 0; j < heatmap.buckets.length; j++) {
      const bucket = heatmap.buckets[j];
      if (!bucket) continue;

      const upperPosition = getExponentialBucketPosition(bucket.upperBound, base);
      const lowerPosition = getExponentialBucketPosition(bucket.lowerBound, base);

      let upperBucketIndex = 0;
      let lowerBucketIndex = 0;
      if (bucket.upperBound < 0) {
        // index into bucket array = offset between bucket position and first negative bucket position
        upperBucketIndex = Math.abs(upperPosition - firstNegativeBucketPosition);
        lowerBucketIndex = Math.abs(lowerPosition - firstNegativeBucketPosition);
      } else if (bucket.lowerBound < 0) {
        upperBucketIndex = numNegativeBuckets + 1;
        lowerBucketIndex = numNegativeBuckets;
      } else {
        // index into bucket array =
        //   number of negative buckets +
        //.  offset between bucket position and first positive bucket position
        upperBucketIndex = numNegativeBuckets + upperPosition - firstPositiveBucketPosition;
        lowerBucketIndex = numNegativeBuckets + lowerPosition - firstPositiveBucketPosition;
        if (zeroBucket) {
          upperBucketIndex++;
          lowerBucketIndex++;
        }
      }

      const bucketSpan = upperBucketIndex - lowerBucketIndex;
      timeSlice.dataPoints.push({
        value: bucket.count,
        bucketIndex: lowerBucketIndex,
        bucketSpan,
      });

      // find the max value PER min bucket (so we divide by bucket span)
      const maxValuePerMinBucket = Math.max(maxValue, bucket.count / bucketSpan);
      if (!isNaN(maxValuePerMinBucket)) {
        maxValue = maxValuePerMinBucket;
      }
    }
  }

  return { maxValue, timeRange, buckets, timeSlices };
}

// Build HeatMapChartData from data with consistent bucket sizes
function processConsistentBuckets(
  timeRange: TimeScale,
  data: HeatMapTimeSeriesData,
  bucketSet: Record<string, HeatMapChartBucket>
) {
  let maxValue = 0;
  const timeSlices: ChartTimeSlice[] = [];

  // derive buckets array from the set, and sort by upper bound
  const buckets: HeatMapChartBucket[] = Object.values(bucketSet);
  buckets.sort((a, b) => a.upper - b.upper);

  // create a dictionary for looking up bucket index
  const bucketIdToIndex: Record<string, number> = {};
  for (let i = 0; i < buckets.length; i++) {
    const bucket = buckets[i];
    if (!bucket) continue;
    bucketIdToIndex[bucketId(bucket.upper)] = i;
  }

  // process datapoints
  for (let i = 0; i < data.heatmap.length; i++) {
    const heatmap = data.heatmap[i];
    if (!heatmap) continue;
    const timeSlice: ChartTimeSlice = { timestamp: heatmap.timestamp, dataPoints: [] };
    timeSlices.push(timeSlice);
    for (let j = 0; j < heatmap.buckets.length; j++) {
      const bucket = heatmap.buckets[j];
      if (!bucket) continue;

      const bucketIndex = bucketIdToIndex[bucketId(bucket.upperBound)];
      if (bucketIndex === undefined) throw new Error('Unknown bucket');

      timeSlice.dataPoints.push({ value: bucket.count, bucketIndex, bucketSpan: 1 });
      if (!isNaN(bucket.count)) {
        maxValue = Math.max(maxValue, bucket.count);
      }
    }
  }

  return { maxValue, timeRange, buckets, timeSlices };
}

type ProcessHeatMapDataOpts = {
  absoluteTimeRange: AbsoluteTimeRange;
};

/**
 * Convert HeatMapTimeSeriesData into HeatMapChartData which is used for rendering the heatmap
 *
 * results: results from the data query
 */
export function processData(
  results: Array<QueryData<'HeatMap'>>,
  { absoluteTimeRange }: ProcessHeatMapDataOpts
): HeatMapChartData {
  if (results.length > 1) throw new Error('Unable to render multiple heatmap data results');

  const startTimeMs = absoluteTimeRange.start.valueOf();
  const endTimeMs = absoluteTimeRange.end.valueOf();

  const result = results[0];
  if (!result || result.isLoading || result.isFetching || !result.data) {
    return {
      maxValue: 0,
      timeRange: { startMs: startTimeMs, endMs: endTimeMs, stepMs: 0, rangeMs: 0 },
      buckets: [],
      timeSlices: [],
    };
  }
  const data = result.data;

  const timeRange: TimeScale = {
    startMs: data.timeRange?.start.valueOf() ?? startTimeMs,
    endMs: data.timeRange?.end.valueOf() ?? endTimeMs,
    stepMs: data.stepMs ?? 0,
    rangeMs: 0,
  };
  timeRange.rangeMs = timeRange.endMs - timeRange.startMs;

  const { bucketSet, isVaryingBucketSizes } = findBuckets(data);

  return isVaryingBucketSizes
    ? processExponentialBuckets(timeRange, data)
    : processConsistentBuckets(timeRange, data, bucketSet ?? {});
}

/**
 * Position within the content of a heatmap chart
 */
export interface Position {
  /**
   * X offset from the left
   */
  x: number;
  /**
   * Y offset from the top
   */
  y: number;
}

/**
 * Information about a particular cell in the heatmap
 */
export interface HeatmapCellInfo {
  /**
   * Timestamp of the cell
   */
  timestamp: number;
  /**
   * Bucket of the cell
   */
  bucket: HeatMapChartBucket;
  /**
   * Value (count) within the cell, null if no data
   */
  value: number | null;
  /**
   * Cell boundary (within the content of a heatmap chart)
   */
  cell: { left: number; top: number; width: number; height: number };
}

/**
 * Find information about the heatmap cell corresponding to a particular x/y position
 *
 * width: width of the heatmap canvas
 * height: height of the heatmap canvas
 * position: position (x, y) of interest within the canvas
 * data: the heatmap chart data
 */
export function heatmapInfoAtPosition(
  width: number,
  height: number,
  position: Position,
  data: HeatMapChartData
): HeatmapCellInfo | null {
  if (width === 0 || height === 0) return null;
  const { startMs, endMs } = data.timeRange;
  const stepMs =
    typeof data.timeRange.stepMs === 'number' && data.timeRange.stepMs > 0
      ? data.timeRange.stepMs
      : MIN_STEP_FALLBACK_MS;
  const cols = 1 + (endMs - startMs) / stepMs;
  const rows = data.buckets.length;

  const colIdx = Math.floor((position.x * cols) / width);
  const rowIdx = Math.floor((position.y * rows) / height);
  if (rowIdx < 0 || colIdx < 0) return null;
  const timestamp = startMs + colIdx * stepMs;

  const bucketIdx = data.buckets.length - rowIdx - 1;
  const rowBucket = data.buckets[bucketIdx];
  if (!rowBucket) return null;
  const bucket = { ...rowBucket };

  const cell = {
    left: (colIdx * width) / cols,
    width: width / cols,
    top: (rowIdx * height) / rows,
    height: height / rows,
  };

  let value = null;
  let someValuesAtTimestamp = false;
  for (let i = 0; i < data.timeSlices.length; i++) {
    const timeSlice = data.timeSlices[i];
    if (!timeSlice) continue;
    if (timeSlice.timestamp === timestamp) {
      if (timeSlice.dataPoints.length > 0) someValuesAtTimestamp = true;
      for (let j = 0; j < timeSlice.dataPoints.length; j++) {
        const dataPoint = timeSlice.dataPoints[j];
        if (!dataPoint) continue;

        // does the bucket span of this datapoint overlap the bucket corresponding to the y position
        if (bucketIdx >= dataPoint.bucketIndex && bucketIdx < dataPoint.bucketIndex + dataPoint.bucketSpan) {
          value = dataPoint.value;

          // update the bucket range based on bucketspan of this datapoint
          const lowerBucket = data.buckets[dataPoint.bucketIndex];
          const upperBucket = data.buckets[dataPoint.bucketIndex + dataPoint.bucketSpan - 1];
          if (lowerBucket && upperBucket) {
            bucket.lower = lowerBucket.lower;
            bucket.upper = upperBucket.upper;
          }

          // update the cell top/height based on bucketspan of this datapoint
          const upperBucketIdx = dataPoint.bucketIndex + dataPoint.bucketSpan - 1;
          const upperRowIdx = data.buckets.length - upperBucketIdx - 1;
          cell.top = (upperRowIdx * height) / rows;
          cell.height = (dataPoint.bucketSpan * height) / rows;
        }
        if (dataPoint.bucketIndex >= bucketIdx) break;
      }
    }
    if (timeSlice.timestamp >= timestamp) break;
  }

  if (value === null) {
    // if there is any data at timestamp we assume that all missing buckets have value 0
    if (someValuesAtTimestamp) value = 0;
  } else {
    // negative values are not valid, we treat them as missing data points
    if (value < 0) value = null;
  }

  return { timestamp, bucket, value, cell };
}

/**
 * Convert a timestamp to a pixel-X coordinate within the heatmap grid.
 * Returns the center of the column that the timestamp falls into,
 * or `undefined` if the timestamp is outside the grid.
 */
export function heatmapTimestampToPixelX(ts: number, timeRange: TimeScale, gridWidth: number): number | undefined {
  const { startMs, endMs, stepMs } = timeRange;
  if (!stepMs || stepMs <= 0 || gridWidth <= 0) return undefined;
  const cols = 1 + (endMs - startMs) / stepMs;
  const colIdx = Math.round((ts - startMs) / stepMs);
  if (colIdx < 0 || colIdx >= cols) return undefined;
  return (colIdx * gridWidth) / cols + gridWidth / (2 * cols);
}

/**
 * Convert x position to a timestamp
 *
 * x: position of interest
 * width: width of the canvas
 * timeRange: time range info
 */
export function xPositionToTimestamp(x: number, width: number, timeRange: TimeScale) {
  const { startMs, endMs } = timeRange;
  const stepMs = typeof timeRange.stepMs === 'number' && timeRange.stepMs > 0 ? timeRange.stepMs : MIN_STEP_FALLBACK_MS;
  const cols = stepMs === 0 ? 0 : 1 + (endMs - startMs) / stepMs;
  const rawIdx = width === 0 ? 0 : Math.floor((x * cols) / width);
  const colIdx = Math.max(0, Math.min(rawIdx, cols - 1));
  const timestamp = startMs + colIdx * stepMs;
  return timestamp;
}

/**
 * Provide a formatted string for the given bucket boundary
 *
 * boundary: bucket boundary, as a string or number
 */
export function formatBucketBoundary(boundary: string | number, unit?: UnitOptions) {
  const value = typeof boundary === 'string' ? Number(boundary) : boundary;
  if (isNaN(value)) return typeof boundary === 'string' ? boundary : 'NaN';
  if (value === 0) return '0';
  const isVerySmallValue = Math.abs(value) < 0.0001;
  // use scientific notation for tiny values in decimal-like contexts
  if (isVerySmallValue && (!unit || isDecimalUnit(unit))) return SCIENTIFIC_NOTATION_FORMATTER.format(value);

  if (unit && isBytesUnit(unit)) {
    const options: FormatValueOptions = {
      ...unit,
      scientificForTinyValues: true,
    };
    return formatValue(value, options);
  }
  return formatValue(value, unit ?? DEFAULT_UNIT);
}
